import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';

import DropdownSelect from '../Visualeffects/DropdownSelect';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';

const getResultBoxClasses = (res) => {
  if (!res) return 'bg-white/80 border border-white/40 text-gray-800 shadow-lg';
  if (res.error) return 'bg-red-50 border border-red-200 text-red-800 shadow-lg';
  if (res.converged) return 'bg-green-50 border border-green-200 text-green-800 shadow-lg';
  return 'bg-amber-50 border border-amber-200 text-amber-700 shadow-lg';
};

const isNaturalNumber = (str) => {
  const num = Number(str);
  return Number.isInteger(num) && num > 0;
};

export default function NewtonSolverUI({ state = {}, setState }) {
  const {
    equation = '',
    startValue = '',
    method = '',
    damping = false,
    dampingValue = 1.0,
    tolerance = '1e-8',
    maxIter = '20',
    loading = false,
    result = null,
    error = null,
    showFullInfo = false,
  } = state;


  const set = (key, value) => setState((prev) => ({ ...prev, [key]: value }));

  const toleranceNumeric = React.useMemo(() => {
    const parsed = Number(tolerance);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [tolerance]);

  const equations = (equation || '').split(',').map((e) => e.trim()).filter((e) => e);
  const numEquations = equations.length;
  const isMultivariate = numEquations > 1;

  const parsedStart = (() => {
    try {
      const val = JSON.parse(startValue);
      return Array.isArray(val) ? val : [val];
    } catch {
      return startValue
        .split(',')
        .map((s) => parseFloat(s.trim()))
        .filter((s) => !isNaN(s));
    }
  })();

  const isValidDamping =
    !isNaN(parseFloat(dampingValue)) &&
    parseFloat(dampingValue) >= 0 &&
    parseFloat(dampingValue) <= 1;
  const validStartVector = parsedStart.length === numEquations;

  const handleSolve = async () => {
    set('loading', true);
    set('result', null);
    set('error', null);

    try {
      const response = await fetch('https://api.numsolve.org/solve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          function: equation,
          start: parsedStart,
          method,
          damping,
          damping_value: parseFloat(dampingValue),
          tolerance: parseFloat(tolerance),
          max_iterations: parseInt(maxIter),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        set('result', data);

        // ⬇️ Log in localStorage schreiben
        if (data?.log && Array.isArray(data.log)) {
          const recentEntry = {
            timestamp: new Date().toISOString(),
            method: 'newton',
            input: {
              function: equation,
              start: parsedStart,
              method,
              damping,
              damping_value: dampingValue,
              tolerance,
              max_iterations: maxIter,
            },
            solution: data.solution,
            residual: data.residuum,
            converged: data.converged,
            log: data.log,
          };

         const existingLogs = JSON.parse(localStorage.getItem('calcLogs') || '[]');
const updatedLogs = [recentEntry, ...existingLogs].slice(0, 100);
localStorage.setItem('calcLogs', JSON.stringify(updatedLogs));
window.dispatchEvent(new Event("calcLogsUpdated"));

        }
      }
      else {
        set('error', data?.error || 'Unexpected error.');
      }


    } catch (err) {
      set('error', 'Backend is not reachable.');
    }

    set('loading', false);
  };

  const inputCard = (
    <div className="rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] backdrop-blur">
      <div className="space-y-4">
        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Function f(x)</label>
          <div className="input-wrapper input-wrapper input-pulse-green">
            <textarea
              value={equation}
              onChange={(e) => set('equation', e.target.value)}
              placeholder="e.g.  x[0]**2 + x[1] - 2, x[0] + x[1]**2 - 2"
              rows={3}
              className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 focus:outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-3 block text-sm font-bold text-gray-700">Initial guess x₀</label>
            <div className="input-wrapper input-wrapper-tight input-pulse-green">
              <Input
                value={startValue}
                setValue={(val) => set('startValue', val)}
                placeholder={'e.g. [1.0, 2.0]'}
              />
            </div>
            {startValue !== '' && !validStartVector && (
              <p className="mt-1 text-sm text-red-500">
                Number of start values must match number of equations ({numEquations}).
              </p>
            )}
          </div>

          <div>
            <label className="mb-3 block text-sm font-bold text-gray-700">Tolerance</label>
            <div className="input-wrapper input-wrapper-tight input-pulse-green">
              <Input value={tolerance} setValue={(val) => set('tolerance', val)} placeholder="e.g. 1e-8" />
            </div>
          </div>

          <div>
            <label className="mb-3 block text-sm font-bold text-gray-700">Max iterations</label>
            <div className="input-wrapper input-wrapper-tight input-pulse-green">
              <Input value={maxIter} setValue={(val) => set('maxIter', val)} placeholder="e.g. 20" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <DropdownSelect
            label="Method"
            color="green"
            value={method}
            onChange={(val) => set('method', val)}
            options={[
              { value: 'newton', label: 'Newton' },
              { value: 'quasi', label: 'Quasi-Newton', disabled: !isMultivariate },
              { value: 'gauss', label: 'Gauss-Newton', disabled: !isMultivariate },
            ]}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={damping}
              onChange={(e) => set('damping', e.target.checked)}
              className="h-4 w-4"
            />
            <label className="text-sm text-gray-600">Enable damping</label>
          </div>
        </div>

        {damping && (
          <Input
            label="Damping factor"
            value={dampingValue}
            setValue={(val) => set('dampingValue', val)}
            placeholder="e.g. 0.7"
          />
        )}

        {damping && !isValidDamping && (
          <p className="mt-1 text-sm text-red-500">Damping factor must be between 0 and 1.</p>
        )}
        <div className="mt-6">
          <button
            className="button button-green"
            onClick={handleSolve}
            disabled={loading || !isNaturalNumber(maxIter) || !validStartVector || (damping && !isValidDamping)}
          >
            {loading ? 'Solving…' : 'Solve'}
          </button>
        </div>
      </div>
    </div>
  );

  const outputCard = (
    <div
      className={`rounded-3xl p-8 transition-all duration-300 ${getResultBoxClasses(result)} ${
        result && result.converged && !result.error ? 'result-success' : ''
      }`}
    >
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Results</h2>

      {result && (
        <div className="space-y-2 text-sm">
          {result.error ? (
            <p>
              <strong>Error:</strong> {result.error}
            </p>
          ) : (
            <>
              <p>
                <strong>Root:</strong> {Array.isArray(result.solution) ? result.solution.join(', ') : result.solution}
              </p>
              <p>
                <strong>Iterations:</strong> {result.iterations}
              </p>
              <p>
                <strong>Converged:</strong> {result.converged ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Final residual:</strong> {result.residuum}
              </p>
            </>
          )}
          {result?.log_file && (
            <div className="mt-4">
              <a
                href={`https://api.numsolve.org.com/${result.log_file}`}
                target="_blank"
                rel="noopener noreferrer"
                className="button button-green inline-block text-center"
              >
                Download Log
              </a>
            </div>
          )}
        </div>
      )}
      {!result && !error && !loading && (
        <div className="text-sm text-gray-500">No results yet. Please enter an equation.</div>
      )}

      {error && !result && (
        <div className="mt-4 rounded-lg bg-red-100 p-3 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result?.residuals && result.residuals.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-md font-semibold">Residual vs. Iteration</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={result.residuals
                .map((r, i) => ({ iteration: i + 1, residual: typeof r === 'number' ? r : Number(r) }))
                .filter((entry) => Number.isFinite(entry.residual) && entry.residual > 0)}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="iteration" />
              <YAxis scale="log" domain={['auto', 'auto']} allowDataOverflow />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="residual"
                stroke="#16a34a"
                dot={{ r: 3, stroke: '#16a34a', fill: '#16a34a' }}
                activeDot={{ r: 5, stroke: '#15803d', fill: '#16a34a' }}
              />
              {toleranceNumeric !== null && (
                <ReferenceLine
                  y={toleranceNumeric}
                  stroke="#34d399"
                  strokeDasharray="6 6"
                  label={{ value: 'Tolerance', position: 'top', fill: '#059669', fontSize: 12 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );

  return (
    <SolverTwoPanelLayout
      loading={loading}
      inputCard={inputCard}
      outputCard={outputCard}
    />
  );
}

function Input({ label, value, setValue, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 whitespace-pre">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2 rounded-xl border border-gray-300 bg-gray-50 text-gray-800 focus:outline-none resize-none" />
    </div>
  );
}


